import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Settings2, Building2, LifeBuoy, ToggleLeft, Archive, Save, Lock,
  Globe, Hash, Mail, Wifi, ShieldCheck, Bell,
} from "lucide-react";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { k: "company",         label: "Company",         Icon: Building2 },
  { k: "general",         label: "General",         Icon: Globe },
  { k: "numbering",       label: "Numbering",       Icon: Hash },
  { k: "support",         label: "Support",         Icon: LifeBuoy },
  { k: "enquiry_routing", label: "Enquiry routing", Icon: Mail },
  { k: "offline_sync",    label: "Offline & sync",  Icon: Wifi },
  { k: "security",        label: "Security",        Icon: ShieldCheck },
  { k: "notifications",   label: "Notifications",   Icon: Bell },
  { k: "features",        label: "Feature flags",   Icon: ToggleLeft },
  { k: "retention",       label: "Retention",       Icon: Archive },
];

const FEATURE_META = [
  { k: "public_job_share",       label: "Public job status share",  hint: "Customers get a share link to track their job." },
  { k: "offline_sync_heartbeat", label: "Offline sync heartbeat",   hint: "PWA reports queue-drain failures back to admin." },
  { k: "voice_notes",            label: "Voice notes on job cards", hint: "Mechanics can attach short voice memos (beta)." },
  { k: "estimate_signature",     label: "Estimate signature",       hint: "Customers e-sign or check-box approve estimates." },
  { k: "whatsapp_share",         label: "WhatsApp share",           hint: "Deep links to WhatsApp for share/notify flows." },
];

const Field = ({ label, children }) => (
  <div className="flex-1">
    <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);
const FieldRow = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
);
const Section = ({ children, testId }) => (
  <div className="bg-white rounded-xl border border-[#E4E7EC] p-5 space-y-3 max-w-2xl" data-testid={testId}>
    {children}
  </div>
);

const ListInput = ({ value = [], onChange, disabled, testId }) => {
  const asString = Array.isArray(value) ? value.join(", ") : (value || "");
  return (
    <Input
      data-testid={testId}
      value={asString}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
      className="rounded-md font-mono"
      placeholder="comma, separated, values"
    />
  );
};

const Toggle = ({ checked, onChange, disabled, testId }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!checked)}
    data-testid={testId}
    disabled={disabled}
    className={`relative h-6 w-11 rounded-full transition-colors ${
      checked ? "bg-[#12B76A]" : "bg-[#E4E7EC]"
    } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
  >
    <span
      className={`absolute top-[2px] h-5 w-5 rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-[22px]" : "translate-x-[2px]"
      }`}
    />
  </button>
);

export default function AdminSettings() {
  const { user: me } = useAuth();
  const canEdit = me?.admin_role === "platform_owner";
  const [tab, setTab] = useState("company");
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/settings").then((r) => { setData(r.data); setDirty(false); }).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, []);

  const setSection = (section, key, value) => {
    setData((prev) => ({ ...prev, [section]: { ...(prev?.[section] || {}), [key]: value } }));
    setDirty(true);
  };
  const setNested = (section, key, subkey, value) => {
    setData((prev) => {
      const sec = { ...(prev?.[section] || {}) };
      const inner = { ...(sec[key] || {}) };
      inner[subkey] = value;
      sec[key] = inner;
      return { ...prev, [section]: sec };
    });
    setDirty(true);
  };

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const body = {};
      TABS.forEach((t) => { if (data[t.k]) body[t.k] = data[t.k]; });
      await api.put("/admin/settings", body);
      toast.success("Platform settings saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="p-6 text-[13px] text-[#667085]">Loading settings…</div>;

  return (
    <div className="space-y-5" data-testid="admin-settings-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Governance</div>
          <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557] flex items-center gap-2">
            <Settings2 size={22} className="text-[#457b9d]" /> Platform Settings
          </h1>
          <p className="text-[13px] text-[#667085] mt-0.5">
            Single source of truth: company · general · numbering · support · enquiry routing · offline & sync · security · notifications · features · retention.
            {data.updated_at && ` Last updated ${fmtDateTime(data.updated_at)} by ${data.updated_by || "system"}.`}
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="admin-settings-save"
            className={`h-9 rounded-md font-semibold text-[13px] px-3 ${
              dirty ? "bg-[#e63946] hover:bg-[#d02f3c] text-white" : "bg-[#E4E7EC] text-[#667085] cursor-not-allowed"
            }`}
          >
            <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </Button>
        )}
      </div>

      {!canEdit && (
        <div className="rounded-md bg-[#F79009]/10 border border-[#F79009]/25 text-[#8a5a00] px-3 py-2 text-[12px] inline-flex items-center gap-2">
          <Lock size={12} /> Read-only view. Only Platform Owner can change platform settings.
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-[#E4E7EC] overflow-x-auto">
        <div className="inline-flex gap-1 flex-nowrap min-w-max">
          {TABS.map((t) => {
            const Icon = t.Icon;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`admin-settings-tab-${t.k}`}
                className={`h-10 px-3 text-[12px] font-semibold border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 whitespace-nowrap ${
                  tab === t.k
                    ? "border-[#e63946] text-[#1d3557]"
                    : "border-transparent text-[#667085] hover:text-[#1d3557]"
                }`}
              >
                <Icon size={13} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* COMPANY */}
      {tab === "company" && (
        <Section testId="admin-settings-company">
          <FieldRow>
            <Field label="Legal / display name">
              <Input data-testid="settings-company-name" value={data.company?.name || ""} disabled={!canEdit}
                onChange={(e) => setSection("company", "name", e.target.value)} className="rounded-md" />
            </Field>
            <Field label="Tagline">
              <Input data-testid="settings-company-tagline" value={data.company?.tagline || ""} disabled={!canEdit}
                onChange={(e) => setSection("company", "tagline", e.target.value)} className="rounded-md" />
            </Field>
          </FieldRow>
          <Field label="Registered address">
            <Textarea rows={2} data-testid="settings-company-address" value={data.company?.address || ""} disabled={!canEdit}
              onChange={(e) => setSection("company", "address", e.target.value)} className="rounded-md" />
          </Field>
          <FieldRow>
            <Field label="GST number">
              <Input data-testid="settings-company-gst" value={data.company?.gst_number || ""} disabled={!canEdit}
                onChange={(e) => setSection("company", "gst_number", e.target.value)} className="rounded-md font-mono" />
            </Field>
            <Field label="Base currency">
              <Input data-testid="settings-company-currency" value={data.company?.currency || ""} disabled={!canEdit}
                onChange={(e) => setSection("company", "currency", e.target.value)} className="rounded-md font-mono" maxLength={3} />
            </Field>
          </FieldRow>
          <Field label="Website">
            <Input data-testid="settings-company-website" value={data.company?.website || ""} disabled={!canEdit}
              onChange={(e) => setSection("company", "website", e.target.value)} className="rounded-md" />
          </Field>
        </Section>
      )}

      {/* GENERAL */}
      {tab === "general" && (
        <Section testId="admin-settings-general">
          <FieldRow>
            <Field label="Default locale">
              <Input data-testid="settings-general-locale" value={data.general?.default_locale || ""} disabled={!canEdit}
                onChange={(e) => setSection("general", "default_locale", e.target.value)} className="rounded-md font-mono" />
            </Field>
            <Field label="Default timezone">
              <Input data-testid="settings-general-tz" value={data.general?.default_timezone || ""} disabled={!canEdit}
                onChange={(e) => setSection("general", "default_timezone", e.target.value)} className="rounded-md font-mono" />
            </Field>
          </FieldRow>
          <Field label="Supported locales (comma-separated)">
            <ListInput testId="settings-general-locales"
              value={data.general?.supported_locales || []} disabled={!canEdit}
              onChange={(v) => setSection("general", "supported_locales", v)} />
          </Field>
          <FieldRow>
            <Field label="Brand primary hex">
              <Input data-testid="settings-general-primary" value={data.general?.brand_primary_hex || ""} disabled={!canEdit}
                onChange={(e) => setSection("general", "brand_primary_hex", e.target.value)} className="rounded-md font-mono" />
            </Field>
            <Field label="Brand accent hex">
              <Input data-testid="settings-general-accent" value={data.general?.brand_accent_hex || ""} disabled={!canEdit}
                onChange={(e) => setSection("general", "brand_accent_hex", e.target.value)} className="rounded-md font-mono" />
            </Field>
          </FieldRow>
        </Section>
      )}

      {/* NUMBERING */}
      {tab === "numbering" && (
        <Section testId="admin-settings-numbering">
          <FieldRow>
            <Field label="Job Card prefix">
              <Input data-testid="settings-numbering-jc" value={data.numbering?.jobcard_prefix || ""} disabled={!canEdit}
                onChange={(e) => setSection("numbering", "jobcard_prefix", e.target.value)} className="rounded-md font-mono" />
            </Field>
            <Field label="Estimate prefix">
              <Input data-testid="settings-numbering-est" value={data.numbering?.estimate_prefix || ""} disabled={!canEdit}
                onChange={(e) => setSection("numbering", "estimate_prefix", e.target.value)} className="rounded-md font-mono" />
            </Field>
            <Field label="Invoice prefix">
              <Input data-testid="settings-numbering-inv" value={data.numbering?.invoice_prefix || ""} disabled={!canEdit}
                onChange={(e) => setSection("numbering", "invoice_prefix", e.target.value)} className="rounded-md font-mono" />
            </Field>
            <Field label="Enquiry prefix">
              <Input data-testid="settings-numbering-enq" value={data.numbering?.enquiry_prefix || ""} disabled={!canEdit}
                onChange={(e) => setSection("numbering", "enquiry_prefix", e.target.value)} className="rounded-md font-mono" />
            </Field>
          </FieldRow>
          <FieldRow>
            <Field label="Sequence padding (digits)">
              <Input data-testid="settings-numbering-padding" type="number" step="1"
                value={data.numbering?.sequence_padding ?? 5} disabled={!canEdit}
                onChange={(e) => setSection("numbering", "sequence_padding", Number(e.target.value) || 0)}
                className="rounded-md font-mono" />
            </Field>
            <div className="flex items-center pt-6">
              <label className="inline-flex items-center gap-2 text-[13px] text-[#1d3557] cursor-pointer">
                <Toggle
                  checked={!!data.numbering?.reset_annually}
                  onChange={(v) => setSection("numbering", "reset_annually", v)}
                  disabled={!canEdit}
                  testId="settings-numbering-reset"
                />
                Reset sequence every calendar year
              </label>
            </div>
          </FieldRow>
        </Section>
      )}

      {/* SUPPORT */}
      {tab === "support" && (
        <Section testId="admin-settings-support">
          <FieldRow>
            <Field label="Support email">
              <Input data-testid="settings-support-email" value={data.support?.email || ""} disabled={!canEdit}
                onChange={(e) => setSection("support", "email", e.target.value)} className="rounded-md" />
            </Field>
            <Field label="Support phone">
              <Input data-testid="settings-support-phone" value={data.support?.phone || ""} disabled={!canEdit}
                onChange={(e) => setSection("support", "phone", e.target.value)} className="rounded-md font-mono" />
            </Field>
          </FieldRow>
          <FieldRow>
            <Field label="WhatsApp number">
              <Input data-testid="settings-support-whatsapp" value={data.support?.whatsapp || ""} disabled={!canEdit}
                onChange={(e) => setSection("support", "whatsapp", e.target.value)} className="rounded-md font-mono" />
            </Field>
            <Field label="SLA response (hours)">
              <Input data-testid="settings-support-sla" type="number" step="1"
                value={data.support?.sla_response_hours ?? 0} disabled={!canEdit}
                onChange={(e) => setSection("support", "sla_response_hours", Number(e.target.value) || 0)}
                className="rounded-md font-mono" />
            </Field>
          </FieldRow>
        </Section>
      )}

      {/* ENQUIRY ROUTING */}
      {tab === "enquiry_routing" && (
        <Section testId="admin-settings-enquiry-routing">
          <FieldRow>
            <Field label="Default owner role">
              <Input data-testid="settings-routing-owner" value={data.enquiry_routing?.default_owner_role || ""} disabled={!canEdit}
                onChange={(e) => setSection("enquiry_routing", "default_owner_role", e.target.value)} className="rounded-md font-mono" />
            </Field>
            <Field label="Auto-close after (days)">
              <Input data-testid="settings-routing-autoclose" type="number" step="1"
                value={data.enquiry_routing?.auto_close_days ?? 14} disabled={!canEdit}
                onChange={(e) => setSection("enquiry_routing", "auto_close_days", Number(e.target.value) || 0)}
                className="rounded-md font-mono" />
            </Field>
          </FieldRow>
          <Field label="Notify channels (comma-separated)">
            <ListInput testId="settings-routing-channels"
              value={data.enquiry_routing?.notify_channels || []} disabled={!canEdit}
              onChange={(v) => setSection("enquiry_routing", "notify_channels", v)} />
          </Field>
          <div className="border border-[#E4E7EC] rounded-md p-3">
            <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold mb-2">Priority SLA (hours)</div>
            <FieldRow>
              {["urgent", "high", "normal", "low"].map((p) => (
                <Field key={p} label={p}>
                  <Input data-testid={`settings-routing-sla-${p}`} type="number" step="1"
                    value={data.enquiry_routing?.priority_sla_hours?.[p] ?? 0} disabled={!canEdit}
                    onChange={(e) => setNested("enquiry_routing", "priority_sla_hours", p, Number(e.target.value) || 0)}
                    className="rounded-md font-mono" />
                </Field>
              ))}
            </FieldRow>
          </div>
        </Section>
      )}

      {/* OFFLINE & SYNC */}
      {tab === "offline_sync" && (
        <Section testId="admin-settings-offline-sync">
          <FieldRow>
            <Field label="Queue max age (hours)">
              <Input data-testid="settings-offline-maxage" type="number" step="1"
                value={data.offline_sync?.queue_max_age_hours ?? 72} disabled={!canEdit}
                onChange={(e) => setSection("offline_sync", "queue_max_age_hours", Number(e.target.value) || 0)}
                className="rounded-md font-mono" />
            </Field>
            <Field label="Heartbeat interval (seconds)">
              <Input data-testid="settings-offline-heartbeat" type="number" step="10"
                value={data.offline_sync?.heartbeat_interval_seconds ?? 60} disabled={!canEdit}
                onChange={(e) => setSection("offline_sync", "heartbeat_interval_seconds", Number(e.target.value) || 0)}
                className="rounded-md font-mono" />
            </Field>
          </FieldRow>
          <FieldRow>
            <Field label="Conflict strategy">
              <select
                data-testid="settings-offline-conflict"
                value={data.offline_sync?.conflict_strategy || "server_wins"}
                disabled={!canEdit}
                onChange={(e) => setSection("offline_sync", "conflict_strategy", e.target.value)}
                className="w-full h-9 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] text-[#1d3557]"
              >
                <option value="server_wins">Server wins</option>
                <option value="last_write_wins">Last write wins</option>
                <option value="manual">Manual resolution</option>
              </select>
            </Field>
            <Field label="Warn after pending count">
              <Input data-testid="settings-offline-warn" type="number" step="1"
                value={data.offline_sync?.warn_after_pending_count ?? 25} disabled={!canEdit}
                onChange={(e) => setSection("offline_sync", "warn_after_pending_count", Number(e.target.value) || 0)}
                className="rounded-md font-mono" />
            </Field>
          </FieldRow>
        </Section>
      )}

      {/* SECURITY */}
      {tab === "security" && (
        <Section testId="admin-settings-security">
          <FieldRow>
            <Field label="Session max age (days)">
              <Input data-testid="settings-security-session" type="number" step="1"
                value={data.security?.session_max_days ?? 14} disabled={!canEdit}
                onChange={(e) => setSection("security", "session_max_days", Number(e.target.value) || 0)}
                className="rounded-md font-mono" />
            </Field>
            <Field label="Password min length">
              <Input data-testid="settings-security-pwmin" type="number" step="1"
                value={data.security?.password_min_length ?? 8} disabled={!canEdit}
                onChange={(e) => setSection("security", "password_min_length", Number(e.target.value) || 0)}
                className="rounded-md font-mono" />
            </Field>
          </FieldRow>
          <FieldRow>
            <Field label="Lockout after failed attempts">
              <Input data-testid="settings-security-lockout" type="number" step="1"
                value={data.security?.lockout_after_failed_attempts ?? 5} disabled={!canEdit}
                onChange={(e) => setSection("security", "lockout_after_failed_attempts", Number(e.target.value) || 0)}
                className="rounded-md font-mono" />
            </Field>
            <Field label="Public share link TTL (days)">
              <Input data-testid="settings-security-share-ttl" type="number" step="1"
                value={data.security?.public_share_link_ttl_days ?? 30} disabled={!canEdit}
                onChange={(e) => setSection("security", "public_share_link_ttl_days", Number(e.target.value) || 0)}
                className="rounded-md font-mono" />
            </Field>
          </FieldRow>
          <Field label="Require 2FA for roles (comma-separated)">
            <ListInput testId="settings-security-2fa-roles"
              value={data.security?.require_2fa_for_roles || []} disabled={!canEdit}
              onChange={(v) => setSection("security", "require_2fa_for_roles", v)} />
          </Field>
        </Section>
      )}

      {/* NOTIFICATIONS */}
      {tab === "notifications" && (
        <Section testId="admin-settings-notifications">
          <div className="grid grid-cols-3 gap-2">
            {[
              { k: "email_enabled",    label: "Email"    },
              { k: "sms_enabled",      label: "SMS"      },
              { k: "whatsapp_enabled", label: "WhatsApp" },
            ].map((c) => (
              <label
                key={c.k}
                className={`p-3 rounded-md border border-[#E4E7EC] flex items-center justify-between ${canEdit ? "cursor-pointer hover:bg-[#F7F9FC]" : ""}`}
              >
                <span className="text-[13px] font-semibold text-[#1d3557]">{c.label}</span>
                <Toggle
                  checked={!!data.notifications?.[c.k]}
                  onChange={(v) => setSection("notifications", c.k, v)}
                  disabled={!canEdit}
                  testId={`settings-notif-${c.k}`}
                />
              </label>
            ))}
          </div>
          <FieldRow>
            <Field label="Send-from email">
              <Input data-testid="settings-notif-from-email" value={data.notifications?.send_from_email || ""} disabled={!canEdit}
                onChange={(e) => setSection("notifications", "send_from_email", e.target.value)} className="rounded-md" />
            </Field>
            <Field label="Send-from name">
              <Input data-testid="settings-notif-from-name" value={data.notifications?.send_from_name || ""} disabled={!canEdit}
                onChange={(e) => setSection("notifications", "send_from_name", e.target.value)} className="rounded-md" />
            </Field>
          </FieldRow>
          <Field label="Weekly snapshot recipients (comma-separated emails)">
            <ListInput testId="settings-notif-recipients"
              value={data.notifications?.weekly_snapshot_recipients || []} disabled={!canEdit}
              onChange={(v) => setSection("notifications", "weekly_snapshot_recipients", v)} />
          </Field>
        </Section>
      )}

      {/* FEATURES */}
      {tab === "features" && (
        <div className="bg-white rounded-xl border border-[#E4E7EC] p-2 max-w-2xl" data-testid="admin-settings-features">
          {FEATURE_META.map((f) => (
            <label
              key={f.k}
              className={`flex items-center justify-between gap-3 p-3 rounded-md ${canEdit ? "cursor-pointer hover:bg-[#F7F9FC]" : ""}`}
            >
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-[#1d3557]">{f.label}</div>
                <div className="text-[11px] text-[#667085] mt-0.5">{f.hint}</div>
              </div>
              <Toggle
                checked={!!data.features?.[f.k]}
                onChange={(v) => setSection("features", f.k, v)}
                disabled={!canEdit}
                testId={`settings-feature-${f.k}`}
              />
            </label>
          ))}
        </div>
      )}

      {/* RETENTION */}
      {tab === "retention" && (
        <Section testId="admin-settings-retention">
          <div className="text-[12px] text-[#667085]">
            Data retention windows. Rows older than these values are eligible for archival (background job — currently
            <strong className="text-[#F79009]"> not yet scheduled</strong>).
          </div>
          <FieldRow>
            <Field label="Audit log (days)">
              <Input data-testid="settings-retention-audit" type="number" step="30"
                value={data.retention?.audit_log_days ?? 365} disabled={!canEdit}
                onChange={(e) => setSection("retention", "audit_log_days", Number(e.target.value) || 0)}
                className="rounded-md font-mono" />
            </Field>
            <Field label="Sync events (days)">
              <Input data-testid="settings-retention-sync" type="number" step="30"
                value={data.retention?.sync_events_days ?? 90} disabled={!canEdit}
                onChange={(e) => setSection("retention", "sync_events_days", Number(e.target.value) || 0)}
                className="rounded-md font-mono" />
            </Field>
            <Field label="Closed support cases (days)">
              <Input data-testid="settings-retention-cases" type="number" step="30"
                value={data.retention?.closed_case_days ?? 180} disabled={!canEdit}
                onChange={(e) => setSection("retention", "closed_case_days", Number(e.target.value) || 0)}
                className="rounded-md font-mono" />
            </Field>
          </FieldRow>
        </Section>
      )}
    </div>
  );
}
